import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { canRankUpSkill, rankUpSkill, getSkillRankMilestones, SKILL_RANKS } from '../game-system/skills/skillProgression';
import { SKILLS } from '../game-system/skills/skillSystem';
import './SkillRankUpDialog.css';

/**
 * Dialog component for ranking up character skills
 */
const SkillRankUpDialog = ({ 
  character, 
  open, 
  onOpenChange, 
  onCharacterUpdate, 
  onRankUp 
}) => {
  const [eligibleSkills, setEligibleSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [rankUpResult, setRankUpResult] = useState(null);
  
  // Find skills eligible for rank up when character data changes or dialog opens
  useEffect(() => {
    if (character && character.skills && open) {
      // Find all skills eligible for rank up
      const eligible = character.skills.filter(skill => 
        canRankUpSkill(character, skill.skill.name)
      );
      
      setEligibleSkills(eligible);
      setSelectedSkill(eligible.length > 0 ? eligible[0] : null);
      setRankUpResult(null);
    }
  }, [character, open]);
  
  // Handle rank up action
  const handleRankUp = () => {
    if (!selectedSkill) return;
    
    const result = rankUpSkill(character, selectedSkill.skill.name);
    
    if (result.success) {
      setRankUpResult(result);
      
      if (onCharacterUpdate) {
        onCharacterUpdate(result.character);
      }
      
      if (onRankUp) {
        onRankUp(result);
      }
    }
  };
  
  // Get milestones for the next rank
  const getMilestones = (skill) => {
    if (!skill) return [];
    
    const nextRank = skill.level + 1;
    return getSkillRankMilestones(nextRank);
  };
  
  // Get the full skill details from our skill system
  const getFullSkillDetails = (skillName) => {
    if (!skillName) return null;
    
    const skillKey = Object.keys(SKILLS).find(
      key => SKILLS[key].name.toLowerCase() === skillName.toLowerCase()
    );
    
    return skillKey ? SKILLS[skillKey] : null;
  };
  
  // Get progress percentage toward next rank
  const getProgressPercentage = (skill) => {
    if (!skill) return 0;
    
    const nextRank = skill.level + 1;
    const requiredXP = nextRank * nextRank * 100; // From skillProgression.js calculation
    return Math.min(100, Math.floor((skill.experience / requiredXP) * 100));
  };
  
  // If there are no eligible skills, show appropriate message
  if (eligibleSkills.length === 0 && open && !rankUpResult) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="skill-rankup-dialog">
          <DialogHeader>
            <DialogTitle>Skill Advancement</DialogTitle>
            <DialogDescription>
              None of your skills have enough experience to rank up yet. 
              Continue using skills during adventures to gain experience.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // If rank up was successful, show the result
  if (rankUpResult && rankUpResult.success) {
    const newRank = SKILL_RANKS[rankUpResult.character.skills.find(
      s => s.skill.name === selectedSkill.skill.name
    ).level];
    
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="skill-rankup-dialog">
          <DialogHeader>
            <DialogTitle className="rankup-success-title">
              Skill Rank Up!
            </DialogTitle>
            <DialogDescription className="rankup-success-description">
              {rankUpResult.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="rankup-result">
            <h3>{selectedSkill.skill.name} is now {newRank.name} rank!</h3>
            
            <div className="new-abilities">
              <h4>New Abilities:</h4>
              <ul>
                {newRank.abilities.map((ability, index) => (
                  <li key={index}>{ability}</li>
                ))}
              </ul>
            </div>
            
            <p className="rank-description">
              {newRank.description}
            </p>
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              setRankUpResult(null);
              
              // If there are more eligible skills, show them
              // Otherwise close the dialog
              if (eligibleSkills.length <= 1) {
                onOpenChange(false);
              } else {
                // Remove the ranked up skill from eligible list
                setEligibleSkills(prev => 
                  prev.filter(s => s.skill.name !== selectedSkill.skill.name)
                );
                setSelectedSkill(eligibleSkills.length > 1 ? 
                  eligibleSkills.find(s => s.skill.name !== selectedSkill.skill.name) : 
                  null
                );
              }
            }}>
              {eligibleSkills.length > 1 ? 'Continue' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Full skill details for selected skill
  const fullSkillDetails = selectedSkill ? 
    getFullSkillDetails(selectedSkill.skill.name) : 
    null;
    
  // Current and next rank for selected skill
  const currentRank = selectedSkill ? 
    SKILL_RANKS[selectedSkill.level] : 
    null;
    
  const nextRank = selectedSkill ? 
    SKILL_RANKS[selectedSkill.level + 1] : 
    null;
  
  // Main dialog display for skill selection and rank up
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="skill-rankup-dialog">
        <DialogHeader>
          <DialogTitle>Skill Advancement</DialogTitle>
          <DialogDescription>
            The following skills have enough experience to rank up. 
            Select a skill to advance.
          </DialogDescription>
        </DialogHeader>
        
        <div className="skill-selection">
          <label htmlFor="skill-select">Select Skill:</label>
          <select 
            id="skill-select"
            value={selectedSkill?.skill.name || ''}
            onChange={(e) => {
              const selected = eligibleSkills.find(
                s => s.skill.name === e.target.value
              );
              setSelectedSkill(selected);
            }}
          >
            {eligibleSkills.map(skill => (
              <option key={skill.skill.name} value={skill.skill.name}>
                {skill.skill.name} ({currentRank?.name} → {nextRank?.name})
              </option>
            ))}
          </select>
        </div>
        
        {selectedSkill && fullSkillDetails && (
          <div className="skill-details">
            <div className="skill-header">
              <h3>{selectedSkill.skill.name}</h3>
              <span className="skill-attribute">({fullSkillDetails.attribute})</span>
            </div>
            
            <p className="skill-description">{fullSkillDetails.description}</p>
            
            <div className="rank-progression">
              <div className="current-rank">
                <span className="rank-label">Current Rank:</span>
                <span className="rank-value">{currentRank.name}</span>
              </div>
              
              <div className="next-rank">
                <span className="rank-label">Next Rank:</span>
                <span className="rank-value">{nextRank.name}</span>
              </div>
              
              <div className="xp-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${getProgressPercentage(selectedSkill)}%` }}
                  />
                </div>
                <span className="progress-text">
                  {selectedSkill.experience} XP - Ready to Advance!
                </span>
              </div>
            </div>
            
            <div className="rank-benefits">
              <h4>New Rank Benefits:</h4>
              <ul>
                {nextRank.abilities.map((ability, index) => (
                  <li key={index}>{ability}</li>
                ))}
              </ul>
              <p className="rank-description">{nextRank.description}</p>
            </div>
            
            <div className="rank-milestones">
              <h4>Milestones for this Rank:</h4>
              <ul>
                {getMilestones(selectedSkill).map((milestone, index) => (
                  <li key={index}>
                    {milestone.description} ({milestone.count} times)
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleRankUp} disabled={!selectedSkill}>
            Rank Up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillRankUpDialog;